import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useInView } from '../../hooks/useInView';

const ServicesPage = () => {
  const { ref: heroRef, isInView: heroInView } = useInView({ threshold: 0.3 });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sinax.co.jp';
    
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "ITコンサルティング・PMO支援・SES事業",
      "provider": {
        "@type": "Organization",
        "name": "株式会社SINAX",
        "url": siteUrl
      },
      "areaServed": {
        "@type": "Place",
        "name": "東京都"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "SINAX サービス",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "SES（システムエンジニアリングサービス）事業",
              "description": "経験豊富なエンジニアのアサイン、プロジェクト状況に応じた柔軟な体制構築"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "ITコンサルティング／PMO支援事業",
              "description": "IT戦略立案、プロジェクトマネジメント支援、システム要件定義・設計支援"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "アプリ開発事業",
              "description": "Webアプリケーション開発、モバイルアプリ開発、UI/UXデザイン"
            }
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(serviceSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      id: 'ses',
      title: 'SES（システムエンジニアリングサービス）事業',
      description: `必要なのは「人」ではなく、「一緒に前に進める存在」だと考えています。SINAXのSESは、スキルだけでなく現場や背景を理解した上で、一緒に成果を出せる人材をアサインします。`,
      details: [
        '経験豊富なエンジニアのアサイン',
        'プロジェクト状況に応じた柔軟な体制構築',
        '技術スキルとコミュニケーション力を兼ね備えた人材',
        '長期的な関係構築を重視したマッチング',
      ],
      icon: 'ri-user-star-line',
      image: 'https://static.readdy.ai/image/4df5c7deddaddbbeeb26e7a7cacf2afd/99e2bf28ea80c3fae555c0ddc1797a99.jpeg',
    },
    {
      id: 'consulting',
      title: (
        <>
          ITコンサルティング／
          <br />
          PMO支援事業
        </>
      ),
      description: `私たちは、外から助言するコンサルティングは行いません。
経営・事業・ITの論点を整理し、意思決定と実行の間に立ち、プロジェクトが前に進み続ける状態をつくることがSINAXの役割です。
構想から実行まで、責任を持って伴走します。`,
      details: [
        'IT戦略立案・ロードマップ策定',
        'プロジェクトマネジメント支援',
        'システム要件定義・設計支援',
        'デジタルトランスフォーメーション推進',
      ],
      icon: 'ri-lightbulb-line',
      image: 'https://static.readdy.ai/image/4df5c7deddaddbbeeb26e7a7cacf2afd/3043b0538d42f3268f8dcb84c690f74d.jpeg',
    },
    {
      id: 'app-development',
      title: 'アプリ開発事業',
      description: `SINAXでは、現場や業務の中にある課題を起点に、
自らプロダクトを構想し、アプリケーションとして形にしていきます。
開発するだけではなく、使われ続けることを前提に、
企画・設計・開発・運用まで一気通貫して向き合います。`,
      details: [
        'Webアプリケーション開発',
        'モバイルアプリ開発（iOS/Android）',
        'UI/UXデザイン',
        '保守・運用サポート',
      ],
      icon: 'ri-smartphone-line',
      image: 'https://static.readdy.ai/image/4df5c7deddaddbbeeb26e7a7cacf2afd/fa10364baed48567c2de479e8c903032.jpeg',
    },
  ];

  return (
    <>
      <Header />
      <main className="pt-20">
        <section
          ref={heroRef}
          className="relative min-h-[60vh] flex items-center justify-center bg-white overflow-hidden"
        >
          <div className="absolute top-10 right-10 w-40 h-40 rounded-full bg-sage opacity-20 blur-2xl" />
          <div className="absolute bottom-20 left-16 w-24 h-24 rounded-full bg-mustard opacity-25" />

          <div className="relative z-10 px-8 lg:px-16 max-w-7xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="font-serif text-6xl lg:text-7xl text-darkgray mb-6 tracking-wide"
            >
              Services
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-sans text-lg text-gray-600"
            >
              3つの事業領域
            </motion.p>
          </div>
        </section>

        {services.map((service, index) => (
          <ServiceDetailSection key={service.id} service={service} index={index} />
        ))}
      </main>
      <Footer />
    </>
  );
};

const ServiceDetailSection = ({
  service,
  index,
}: {
  service: any;
  index: number;
}) => {
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const isEven = index % 2 === 0;

  return (
    <section
      id={service.id}
      ref={ref}
      className={`relative py-24 lg:py-32 ${isEven ? 'bg-white' : 'bg-cream'} overflow-hidden`}
    >
      {index === 0 && (
        <>
          <div className="absolute top-32 right-20 w-56 h-56 rounded-full bg-mustard opacity-15 blur-3xl" />
          <div className="absolute bottom-10 left-10 w-32 h-32 rounded-full bg-sage opacity-20" />
        </>
      )}
      {index === 1 && (
        <div className="absolute top-1/2 left-1/4 w-48 h-48 rounded-full bg-cream opacity-30 blur-2xl" />
      )}
      {index === 2 && (
        <div className="absolute bottom-20 right-1/3 w-36 h-36 rounded-full bg-sage opacity-25 blur-xl" />
      )}

      <div className="relative z-10 px-8 lg:px-16 max-w-7xl mx-auto">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
            isEven ? '' : 'lg:flex-row-reverse'
          }`}
        >
          <motion.div
            initial={{ opacity: 0, x: isEven ? -30 : 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className={isEven ? '' : 'lg:order-2'}
          >
            <div className="relative rounded-lg overflow-hidden aspect-[4/3]">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-sage opacity-10" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: isEven ? 30 : -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={isEven ? '' : 'lg:order-1'}
          >
            <div className="w-16 h-16 flex items-center justify-center mb-6">
              <i className={`${service.icon} text-5xl text-sage`}></i>
            </div>
            <h2 className="font-sans text-3xl lg:text-4xl text-darkgray mb-6 leading-relaxed">
              {service.title}
            </h2>
            <p className="font-sans text-base text-gray-600 leading-loose mb-8">
              {service.description}
            </p>
            <ul className="space-y-3 mb-8">
              {service.details.map((detail: string, i: number) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-mustard mt-2 flex-shrink-0" />
                  <span className="font-sans text-sm text-gray-600">{detail}</span>
                </li>
              ))}
            </ul>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 text-sm text-darkgray font-sans cursor-pointer"
            >
              お問い合わせ
              <i className="ri-arrow-right-line"></i>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServicesPage;